import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import { CheckCircleRounded, CloseOutlined, DeleteOutline, 
  ErrorOutline, InfoOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { createPortal } from 'react-dom';
import { styled } from '@mui/material/styles';
import RoutesList, { RouteItem } from './RoutesList/RoutesList';
import { RouteMap } from './RouteMap/RouteMap';
import { useDispatch, useSelector } from 'react-redux';
import { SetOptimizationData } from '@/actions/common/commonPure';
import { RootState } from '@/index';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  pointerWithin,
  DragOverlay
} from '@dnd-kit/core';
import { sortBy } from 'underscore';
import { katys100colors } from '@/components/Common/colorGen';
import { LatLngTuple } from 'leaflet';
import utilMaps from '@/components/utils/utilMaps';
import { setDayData } from '@/actions/routes/routesPure';
import UnplannedShipmentsList from '../UnplannedShipmentsList';
import { useSettings } from '@/components/Settings/SettingsContext';
import { useEditMode } from '@/contexts/EditModeContext';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: any) => ({
  dialogPaper: {
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: theme.shadows[6],
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 7,
      backgroundColor: '#ef5927',
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '29px 9px 10px 30px',
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  diamondBox: {
    width: 32,
    height: 32,
    backgroundColor: '#ef5927',
    boxShadow: '0 0 0 8px rgba(239, 89, 39, 0.4);',
    borderRadius: 4,
    transform: 'rotate(45deg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  warningIcon: {
    transform: 'rotate(-45deg)',
    color: '#ffffff',
    fontSize: 20,
  },
  titleText: {
    marginLeft: '17px',
    fontSize: '19px',
    color: '#000000'
  },
  dialogContent: {
    padding: '16px 89px 0px',
  },
  dividerTop: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  dialogmsg: {
    color: 'grey',
    fontSize: '14px',
    marginBottom: '10px'
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
  },
  toastAlert: {
    '& .MuiAlert-icon': {
      color: '#fff !important',
      opacity: 1,
    },
    '& .MuiAlert-icon .MuiSvgIcon-root': {
      color: '#fff !important',
    },
    '& .MuiAlert-action .MuiIconButton-root': {
      color: '#fff !important',
      '&:hover': { color: '#fff !important' },
    },
    '& .MuiAlert-action .MuiSvgIcon-root': {
      color: '#fff !important',
    },
  },
}));

const HEADER_HEIGHT = 64;
const ROUTES_WIDTH = 436;
const UNPLANNED_WIDTH = 436;
const GUTTER = 8;

const Routes: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const classes = useStyles();
  // UI state
  const [hideRoutes, setHideRoutes] = useState(false);
  const [dragEnabled, setDragEnabled] = useState(false);
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const { editMode, setEditMode } = useEditMode();
  const [dirty, setDirty] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedCount = selectedIds.length;
  const [showList, setShowList] = React.useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { settings } = useSettings();
  const [isDragging, setIsDragging] = useState(false);
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set());
  const [pendingAssignments, setPendingAssignments] = useState<Record<string, any[]>>({});
  const [toast, setToast] = useState<{
    open: boolean; msg: string;
    severity: 'success' | 'error' | 'info'
  }>
    ({ open: false, msg: '', severity: 'success' });

  // Dialog state (for assign to vehicle)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vehicleChoice, setVehicleChoice] = useState<string | null>(null);
  const [confirmData, setConfirmData] = useState<{ ids: string[] }>({ ids: [] });

  // Delete Dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const openDeleteDialog = () => setDeleteOpen(true);
  const closeDeleteDialog = () => setDeleteOpen(false);
  // sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  const SafeAlert = styled(Alert, {
    shouldForwardProp: (prop) => prop !== 'ownerState',
  })({});

  // redux data selectors (PEHLE)
  const { optimization_data, selected_process } = useSelector(
    (state: RootState) => state.core
  );
  const dayData = useSelector((state: RootState) => state.routes);

  // memo: unplanned shipments (PHIR)
  const unplannedShipments = useMemo(
    () => optimization_data?.unplanned_shipments ?? [],
    [optimization_data?.unplanned_shipments]
  );

  const unplannedById = useMemo(() => {
    const map: Record<string, any> = {};
    (unplannedShipments || []).forEach((s: any, i: number) => {
      const key = String(s?.custom_id ?? s?.customer_number ?? i);
      map[key] = s;
    });
    return map;
  }, [unplannedShipments]);

  const routeDetails: Record<string, any> = optimization_data?.routes || {};
  const vehicleDetails: any[] = optimization_data?.vehicles || [];
  const routeIds = useMemo(() => Object.keys(routeDetails), [routeDetails]);

  const assignShipmentsToVehicle = React.useCallback(
    async (shipmentIds: string[], vehicleId: string) => {
      if (!shipmentIds?.length || !vehicleId) return;
      try {
        setDirty(true);
        setAssignedIds(prev => {
          const next = new Set(prev);
          shipmentIds.forEach(id => next.add(id));
          return next;
        });
        setPendingAssignments(prev => {
          const next = { ...prev };
          const current: any[] = Array.isArray(next[vehicleId]) ? [...next[vehicleId]] : [];
          const have = new Set(current.map(s => String(s?.custom_id ?? s?.customer_number)));

          shipmentIds.forEach(id => {
            const obj = unplannedById[String(id)];
            if (obj && !have.has(String(obj.custom_id ?? obj.customer_number))) {
              current.push(obj);
            }
          });

          next[vehicleId] = current;
          return next;
        });

        setToast({ open: true, msg: `${shipmentIds.length} shipment(s) assigned to "${vehicleId}"`, severity: 'success' });
      } catch (e: any) {
        setToast({ open: true, msg: e?.message || 'Failed to assign shipments', severity: 'error' });
      }
    },
    [unplannedById]
  );
  const handleDragEnd = React.useCallback((evt: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = evt;

    if (!over) return;

    const isUnplanned = active?.data?.current?.type === 'unplanned';
    const ids = (active?.data?.current?.ids as string[]) ?? [];
    const vehicleId = String(over?.id ?? '');

    if (isUnplanned && ids.length) {
      setConfirmData({ ids });
      setVehicleChoice(vehicleId || null); // prefill with drop target, user can change
      setDialogOpen(true);
    }
  }, []);

  const handleDialogClose = React.useCallback(() => {
    setDialogOpen(false);
    setVehicleChoice(null);
    setConfirmData({ ids: [] });
  }, []);

  const handleDialogAssign = React.useCallback(() => {
    if (vehicleChoice && confirmData.ids.length) {
      assignShipmentsToVehicle(confirmData.ids, vehicleChoice);
    }
    handleDialogClose();
  }, [vehicleChoice, confirmData, assignShipmentsToVehicle, handleDialogClose]);

  const rightWidth = useMemo(() => {
    if (hideRoutes) return showList ? UNPLANNED_WIDTH : 0;
    return editMode && showList
      ? ROUTES_WIDTH + UNPLANNED_WIDTH + GUTTER
      : ROUTES_WIDTH;
  }, [hideRoutes, editMode, showList]);

  const handleSave = () => {
    setDirty(false);
    setEditMode(false);
    setToast({ open: true, msg: 'Changes saved', severity: 'success' });
  };
  const handleCancel = () => {
    setDirty(false);
    setEditMode(false);
    setToast({ open: true, msg: 'Changes discarded', severity: 'info' });
  };

  // --------- DELETE HELPERS (NEW) ---------
  function extractShipmentsFromVehicleRoutes(routesForVehicle: any[]): any[] {
    const moved: any[] = [];
    if (!Array.isArray(routesForVehicle)) return moved;

    for (const r of routesForVehicle) {
      if (Array.isArray(r?.shipments)) {
        moved.push(...r.shipments);
      }
      if (Array.isArray(r?.stops)) {
        for (const s of r.stops) {
          if (s?.shipment) moved.push(s.shipment);
          if (Array.isArray(s?.shipments)) moved.push(...s.shipments);
        }
      }
    }
    // de-dup (by custom_id/customer_number if available)
    const seen = new Set<string>();
    const unique: any[] = [];
    for (const s of moved) {
      const key = String(s?.custom_id ?? s?.customer_number ?? Math.random());
      if (!seen.has(key)) { seen.add(key); unique.push(s); }
    }
    return unique;
  }

  const handleDelete = async () => {
    if (!selectedCount) return;
    try {
      const opt: any = optimization_data || {};
      const nextRoutes: Record<string, any> = { ...(opt.routes || {}) };
      const nextVehicles: any[] = Array.isArray(opt.vehicles) ? [...opt.vehicles] : [];
      const nextUnplanned: any[] = Array.isArray(opt.unplanned_shipments) ? [...opt.unplanned_shipments] : [];

      const nextRouteStrings: Record<string, any> = { ...(dayData as any)?.routestrings ?? {} };
      const nextColors: Record<string, any> = { ...(dayData as any)?.colors ?? {} };

      for (const vehicleId of selectedIds) {
        const routesForVehicle = nextRoutes[vehicleId] || [];
        const moved = extractShipmentsFromVehicleRoutes(routesForVehicle).map((s) => ({
          ...s,
          reason: 'Vehicle removed',
        }));
        if (moved.length) nextUnplanned.push(...moved);

        // map cleanup (remove polyline group + tour colors)
        delete nextRouteStrings[vehicleId];
        if (Array.isArray(routesForVehicle)) {
          for (const r of routesForVehicle) {
            if (r?.id && nextColors[r.id]) delete nextColors[r.id];
          }
        }

        // remove this vehicle's routes bucket
        delete nextRoutes[vehicleId];

        // remove matching vehicle object (by name as your list derives it)
        const detailsArray = routeDetails[vehicleId] || [];
        const vName = Array.isArray(detailsArray) ? detailsArray[0]?.vehicle_name : detailsArray?.vehicle_name;
        if (vName) {
          const idx = nextVehicles.findIndex((v) => v?.name === vName);
          if (idx >= 0) nextVehicles.splice(idx, 1);
        }
      }

      const nextOpt = {
        ...opt,
        routes: nextRoutes,
        vehicles: nextVehicles,
        unplanned_shipments: nextUnplanned,
      };

      dispatch(SetOptimizationData(nextOpt));

      dispatch(setDayData({
        ...dayData,
        routestrings: nextRouteStrings,
        colors: nextColors,
      }));

      setSelectedIds([]);
      setDirty(false);
      setToast({ open: true, msg: 'Vehicle(s) deleted. Shipments moved to Unplanned.', severity: 'success' });
    } catch (e: any) {
      setToast({ open: true, msg: e?.message || 'Failed to delete vehicles', severity: 'error' });
    }
  };

  useEffect(() => {
    const st = (location.state || {}) as { editMode?: boolean };
    if (st.editMode) {
      setEditMode(true);
      window.history.replaceState({}, '');
    }
  }, [location.state, setEditMode]);

  const routes: RouteItem[] = useMemo(() => {
    const entries = Object.entries(routeDetails);
    const sorted = sortBy(entries, ([id]) => id.toLowerCase());
    return sorted.map(([id], idx) => ({
      id,
      icon: 'truck',
      color: katys100colors[idx % katys100colors.length],
    }));
  }, [routeDetails]);

  const tourColorsById: Record<string, string> = useMemo(() => {
    const colors: Record<string, string> = {};
    let colorIndex = 0;

    Object.entries(routeDetails).forEach(([_, tours]) => {
      if (Array.isArray(tours)) {
        tours.forEach((tour: any) => {
          if (tour?.id) {
            colors[tour.id] = katys100colors[colorIndex % katys100colors.length];
            colorIndex++;
          }
        });
      }
    });

    return colors;
  }, [routeDetails]);

  const handleMapSelect = (routeOrSpots: number | number[]) => {
    const vehicleIds = Object.keys(routeDetails);
    if (typeof routeOrSpots === 'number') {
      setSelectedId(vehicleIds[routeOrSpots]);
    } else if (Array.isArray(routeOrSpots) && routeOrSpots.length) {
      const first = routeOrSpots[0];
      const match = vehicleIds.find((id) =>
        (routeDetails[id]?.stops || []).some((s: any) => s.idNumber === first)
      );
      if (match) setSelectedId(match);
    }
  };

  //  Decode polyline routes and preserve Timeline state
  useEffect(() => {
    if (
      !selected_process?.optimization_id ||
      !optimization_data?.routes ||
      Object.keys(optimization_data.routes).length === 0
    ) {
      return;
    }

    const decodedPolylinesByVehicle: {
      [vehicleName: string]: { id: string; coords: LatLngTuple[] }[];
    } = {};

    for (const [vehicleName, routes] of Object.entries(optimization_data.routes)) {
      if (!Array.isArray(routes)) continue;

      const vehiclePolylines: { id: string; coords: LatLngTuple[] }[] = [];

      routes.forEach((route: any) => {
        if (!route?.id || !route?.polyline) return;

        try {
          const decoded = utilMaps.assemblePolyline(
            { route_polyline: route.polyline, id: route.id },
            ''
          );

          const coords = decoded.coordinates.map(
            (pt: any) => [pt.lng, pt.lat]
          ) as LatLngTuple[];

          vehiclePolylines.push({ id: route.id, coords });
        } catch (err) {
          console.error(`Failed to decode polyline for route ${route.id}`, err);
        }
      });

      decodedPolylinesByVehicle[vehicleName] = vehiclePolylines;
    }

    const allRoutes = Object.values(decodedPolylinesByVehicle).flat();
    const colors = allRoutes.reduce((acc, { id }, idx) => {
      acc[id] = { color: katys100colors[idx % katys100colors.length] };
      return acc;
    }, {} as Record<string, { color: string }>);

    const depotLocation = optimization_data?.depot_location;

    dispatch(setDayData({
      ...dayData,
      routestrings: decodedPolylinesByVehicle,
      colors,
      available: true,
      depotLocation: depotLocation || (dayData as any).depotLocation, // NOTE: DayDataType me depotLocation?: any add karo
    }));

  }, [optimization_data?.routes, selected_process?.optimization_id]);

  useEffect(() => {
    dispatch(setDayData({
      pendingAssignments,
      assignedIds: Array.from(assignedIds),
    }));
  }, [pendingAssignments, assignedIds, dispatch]);

  // CustomToast

  const CustomToast: React.FC<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
    autoHideMs?: number;
    onClose: () => void;
  }> = ({ open, message, severity, autoHideMs = 2500, onClose }) => {
    const classes = useStyles();

    useEffect(() => {
      if (!open) return;
      const t = setTimeout(onClose, autoHideMs);
      return () => clearTimeout(t);
    }, [open, autoHideMs, onClose]);

    if (!open) return null;

    return createPortal(
      <Box sx={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 1400, pointerEvents: 'none' }}>
        <Box sx={{ pointerEvents: 'auto' }}>
          <Alert
            severity={severity}
            variant="filled"
            onClose={onClose}
            className={classes.toastAlert}
            iconMapping={{
              success: <CheckCircleRounded fontSize="small" sx={{ color: '#fff' }} />,
              error: <ErrorOutline fontSize="small" sx={{ color: '#fff' }} />,
              info: <InfoOutlined fontSize="small" sx={{ color: '#fff' }} />,
              warning: <WarningAmberOutlined fontSize="small" sx={{ color: '#fff' }} />,
            }}
            action={
              <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
                <CloseOutlined fontSize="small" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Box>
      </Box>,
      document.body
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {/* Map */}
      <Box sx={{ flex: 1, position: 'relative', zIndex: 0, minWidth: 0 }}>
        <RouteMap
          onSelect={handleMapSelect}
          routeColors={tourColorsById}
          resizeDeps={[showList, editMode, hideRoutes]}
          visibleRouteIds={selectedIds}
        />
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setIsDragging(false)}
      >
        <Box
          sx={{
            height: '100%',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'row',
            gap: `${GUTTER}px`,
            width: `${rightWidth}px`,
            position: 'relative',
          }}
        >
          {/* Left column: RoutesList */}
          {!hideRoutes && (
            <Box sx={{ width: `${ROUTES_WIDTH}px`, height: '100%', minWidth: 0, overflowY: 'auto' }}>
              <RoutesList
                editMode={editMode}
                routes={routes}
                selectedId={selectedId}
                onSelect={(r) => setSelectedId(r.id)}
                routeDetails={routeDetails}
                vehicleDetails={vehicleDetails}
                tourColorsById={tourColorsById}
                onSelectionChange={setSelectedIds}
                hidden={hideRoutes}
                pendingAssignments={pendingAssignments}
                //onDelete={openDeleteDialog}
              />
            </Box>
          )}

          {/* Right column: Unplanned */}
          {editMode && showList && (
            <Box sx={{
              width: `${UNPLANNED_WIDTH}px`,
              height: '100%',
              minWidth: 0,
              overflow: isDragging ? 'visible' : 'hidden',
              bgcolor: 'background.paper',
              borderLeft: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              padding: 0,
              position: 'relative',
              zIndex: 1,
              pointerEvents: isDragging ? 'none' : 'auto',
            }}>
              <UnplannedShipmentsList
                shipments={unplannedShipments}
                dimensionLabels={settings.load_dimensions}
                maxHeight={704}
                onClose={() => setShowList(false)}
                onFilteringChange={(active, count) => {
                  setHideRoutes(active);
                  setFilteredCount(active ? count : null);
                  setDragEnabled(false);
                }}
                onAssignShipments={assignShipmentsToVehicle}
                routeIds={routeIds}
                onReadyToAssign={() => {
                  setHideRoutes(false);
                  setDragEnabled(true);
                }}
                routesHidden={hideRoutes}
                dragEnabled={dragEnabled}
                assignedIds={assignedIds}
              />
            </Box>
          )}
        </Box>
        <DragOverlay dropAnimation={null} />
      </DndContext>

      {/* Delete Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="sm"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.titleLeft}>
            <Box className={classes.diamondBox}>
              <DeleteOutline className={classes.warningIcon} />
            </Box>
            <Typography variant="subtitle2" color="textSecondary" className={classes.titleText}>
              Delete vehicle
            </Typography>
          </Box>
          <IconButton onClick={closeDeleteDialog} size="small">
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Typography variant="subtitle2" color="textSecondary" className={classes.dialogmsg}>
            Are you sure you want to delete {selectedCount === 1 ? 'this vehicle' : `${selectedCount} vehicles`}?
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" className={classes.dialogmsg}>
            Once deleted, all of its routes will be removed and the shipments will automatically move to <strong>Unplanned</strong>.
          </Typography>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button variant="outlined" onClick={closeDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => { await handleDelete(); closeDeleteDialog(); }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign to vehicle Dialog (your requested UI) */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.titleLeft}>
            <Box className={classes.diamondBox}>
              <WarningAmberOutlined className={classes.warningIcon} />
            </Box>
            <Typography variant="subtitle2" color="textSecondary" className={classes.titleText}>
              Confirm assignment
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} size="small">
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <Typography variant="subtitle2" color="textSecondary" className={classes.dialogmsg}>
            Do you want to assign {confirmData.ids.length}{' '}
            {confirmData.ids.length === 1 ? 'shipment' : 'shipments'} to vehicle "{vehicleChoice}"?
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" className={classes.dialogmsg}>
            They will be moved from Unplanned into this route.
          </Typography>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button variant="outlined" onClick={handleDialogClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDialogAssign}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <CustomToast
        open={toast.open}
        message={toast.msg}
        severity={toast.severity}
        autoHideMs={2500}
        onClose={() => setToast(p => ({ ...p, open: false }))}
      />
    </Box>
  );
};

export default Routes;